import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useMemo, 
  useEffect,
  useRef,
  ReactNode 
} from 'react';
import { useAuth } from './AuthContext';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from "convex/react";
import { useToast } from './ToastContext';

// Types
import { ConvexChat, MinimizedChat } from '../types/chat';
import { Space } from '../types/space';
import { ConvexMessage, MessageDocument } from '../types/message';
import { Resource } from '../types/resource';
// import { Mode } from '../types/mode';

// Hooks
import { useMessageGroups } from '../hooks/useMessageGroups';
import { isTempMessageId } from '../utils/messageIds';

// Helpers
import { useAnalytics } from '../hooks/useAnalytics';

// Consolidated interface for all app data and UI state
interface AppDataContextType {
  // ===== CHAT DATA =====
  chats: MinimizedChat[];
  isChatsLoaded: boolean;
  isChatsLoading: boolean;
  currentChat: ConvexChat | null;
  isCurrentChatLoading: boolean;
  chatNotExist: boolean;
  messages: ConvexMessage[];
  isLoadingMessages: boolean;
  messagesError: Error | null;
  currentChatId: string | null;
  
  // ===== SPACE DATA =====
  spaces: Space[];
  isSpacesLoading: boolean;
  isSpaceChatsLoading: boolean;
  filteredSpaces: Space[];
  currentSpace: Space | null;
  currentSpaceId: string;
  currentChatSpace: Space | null;
  currentSpaceChats: MinimizedChat[];
  spaceAttachments: Resource[];

  // ===== UI STATE =====
  // Chat UI
  isSpaceChat: boolean;
  isSpaceView: boolean;
  memoizedChatTitle: string;
  quotedContent: string;
  
  // Modals
  isSearchModalOpen: boolean;
  showSalesModal: boolean;
  salesModalFeature: string;
  isUploadModalOpen: boolean;
  
  // Editing states
  isEditingTitle: boolean;
  editingMessageId: string | null;
  title: string;
  instructions: string;
  isCreatingSpace: boolean;
  
  // Message branching
  activeBranches: Map<string, number>;
  globalBranchCount: number;
  branchesMap: Map<string, number[]>;
  filteredMessages: ConvexMessage[];
  maxBranchId: number;
  
  // Streaming & generation
  isStreaming: boolean;
  isSubmitting: boolean;
  isGeneratingResponse: boolean;
  currentThinkingTime: number;
  useReasoning: boolean;
  useWeb: boolean;
  webCapable: boolean;
  resourceCapable: boolean;
  
  // Incognito (private chat)
  isIncognito: boolean;


  // ===== ACTIONS =====
  // Chat actions
  addNewChat: (spaceId?: string) => Promise<{ id: string }>;
  removeChat: (id: string) => Promise<void>;
  renameChat: (id: string, newTitle: string) => Promise<void>;
  addChatToSpace: (id: string, spaceId: string) => Promise<void>;
  removeChatFromSpace: (id: string) => Promise<void>;
  
  // Space actions
  addResourceToSpace: (spaceId: string, artifactContent: string) => Promise<void>;
  addNewSpace: (title?: string, instructions?: string) => Promise<Space>;
  updateSpaceTitle: (spaceId: string, title: string) => Promise<void>;
  updateSpaceInstructions: (spaceId: string, instructions: string) => Promise<void>;
  updateSpaceKnowledgeCount: (spaceId: string, knowledgeCount: number) => Promise<void>;
  deleteSpace: (spaceId: string) => Promise<void>;
  addTextAttachmentsToSpace: (spaceId: string, docs: MessageDocument[]) => Promise<void>;
  removeTextAttachmentFromSpace: (spaceId: string, url: string) => Promise<void>;
  
  // Message actions
  setMessages: React.Dispatch<React.SetStateAction<ConvexMessage[]>>;
  retryMessages: () => void;
  startStreaming: () => void;
  rateMessage: (messageId: string, status: 'like' | 'dislike') => Promise<void>;
  
  // UI actions
  setActiveBranchForPair: (messagePairId: string, branchId: number) => void;
  setIsSpaceChat: (isSpaceChat: boolean) => void;
  setMemoizedChatTitle: React.Dispatch<React.SetStateAction<string>>;
  setQuotedContent: React.Dispatch<React.SetStateAction<string>>;
  setIsSearchModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSalesModalFeature: (feature: string) => void;
  setShowSalesModal: (show: boolean) => void;
  closeSalesModal: () => void;
  openUploadModal: () => void;
  closeUploadModal: () => void;
  setIsEditingTitle: (editing: boolean) => void;
  setEditingMessageId: (messageId: string | null) => void;
  setTitle: (title: string) => void;
  setInstructions: (instructions: string) => void;
  setIsCreatingSpace: (creating: boolean) => void;
  setGlobalBranchCount: (newCount: number) => void;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentThinkingTime: React.Dispatch<React.SetStateAction<number>>;
  syncThinkingTime: (time: number) => void;
  toggleReasoning: () => void;
  toggleWebSearch: () => void;
  setUseWeb: React.Dispatch<React.SetStateAction<boolean>>;
  setWebCapable: (value: boolean) => void;
  setResourceCapable: (value: boolean) => void;
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>;
  togglePinChat: (id: string) => Promise<void>;
  setCurrentChat: React.Dispatch<React.SetStateAction<ConvexChat | null>>;
  setCurrentChatSpace: React.Dispatch<React.SetStateAction<Space | null>>;
  setCurrentSpace: (space: Space | null) => void;
  
  // Space-specific actions
  handleUpdateSpaceInstructions: () => Promise<void>;
  handleUpdateSpaceTitle: () => Promise<void>;
  handleCreateSpaceChat: (isInChat?: boolean) => Promise<void>;
  handleUpdateSpaceKnowledgeCount: (knowledgeCount: number) => Promise<void>;

  // Incognito actions
  enterIncognito: () => void;
  exitIncognito: () => void;
}

// Synthetic chat id used by optimistic incognito messages — backend ignores it.
export const INCOGNITO_CHAT_ID = 'incognito';

// First path segments that are real routes, not chat IDs. AppDataProvider sits
// above <Routes>, so `useParams().chatId` is always undefined here and the
// chatId is inferred from the URL. Without this set, paths like `/settings`
// were being treated as chatId="settings" — which made `isCurrentChatLoading`
// stuck `true` and prevented the chat-cleanup effect from firing when leaving
// a chat for a non-chat route. Keep in sync with the routes in App.tsx.
const RESERVED_ROOT_PATHS = new Set([
  'new',
  'undefined',
  'privacy-policy',
  'terms-of-service',
  'signup',
  'login',
  'pricing',
  'payment-confirmation',
  'yorku',
  'umanitoba',
  'student',
  'google-drive',
  'chats',
  'spaces',
  'settings',
  'share',
]);

function isValidChatId(chatId: string): boolean {
  if (typeof chatId !== 'string' || chatId.length <= 10) return false;
  const hasLetter = /[a-zA-Z]/.test(chatId);
  const hasNumber = /\d/.test(chatId);
  return hasLetter && hasNumber;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

interface AppDataProviderProps {
  children: ReactNode;
}

export const AppDataProvider: React.FC<AppDataProviderProps> = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { trackChatDeleted, trackChatRenamed, trackSpaceCreated, trackSpaceDeleted, trackSpaceChatCreated } = useAnalytics();
  
  // Route handling
  const params = useParams<{ chatId?: string; currentSpaceId?: string }>();
  const routeMatch = /^\/([a-zA-Z0-9-]+)(?:\/|$)/.exec(location.pathname);
  const candidateChatId = params.chatId || (routeMatch ? routeMatch[1] : undefined);
  // Reject reserved root paths so they don't masquerade as chat IDs (e.g.
  // `/settings` would otherwise leave `currentChat` stale and lock
  // `isCurrentChatLoading` at `true` forever).
  const chatId = candidateChatId && !RESERVED_ROOT_PATHS.has(candidateChatId)
    ? candidateChatId
    : undefined;

  const currentSpaceId = useMemo(() => {
    const pathParts = location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    if (params.currentSpaceId) {
      return params.currentSpaceId;
    }
    const idx = pathParts.findIndex(p => p === 'spaces' || p === 'space');
    return idx !== -1 && idx + 1 < pathParts.length ? pathParts[idx + 1] : '';
  }, [params.currentSpaceId, location.pathname]);
  
  // ===== CONVEX MUTATIONS =====
  const createChatMutation = useMutation("chats:createChat" as any);
  const updateChatMutation = useMutation("chats:updateChat" as any);
  const deleteChatMutation = useMutation("chats:deleteChat" as any);
  const createSpaceMutation = useMutation("spaces:createSpace" as any);
  const updateSpaceMutation = useMutation("spaces:updateSpace" as any);
  const deleteSpaceMutation = useMutation("spaces:deleteSpace" as any);
  const addSpaceAttachmentsMutation = useMutation("spaces:addSpaceAttachments" as any);
  const removeSpaceAttachmentMutation = useMutation("spaces:removeSpaceAttachment" as any);
  const appendSpaceResourceMutation = useMutation("spaces:appendSpaceResource" as any);
  const pinChatMutation = useMutation("chats:pinChat" as any);
  const unpinChatMutation = useMutation("chats:unpinChat" as any);
  const rateMessageMutation = useMutation("messages:rateMessage" as any);
  const reportMessageMutation = useMutation("reportedMessages:create" as any);
  
  // ===== CONVEX QUERIES =====
  const convexChats: MinimizedChat[] = useQuery(
    "chats:getPaginatedChats" as any,
    auth.user?._id && auth.isAuthenticated ? { 
      userId: String(auth.user._id),
      limit: 250,
      legacyUserId: auth.user.legacyId
    } : "skip"
  );
  
  const fullCurrentChat: ConvexChat | null = useQuery(
    "chats:getChatByIdAndUserId" as any,
    chatId && chatId !== 'new' && auth.isAuthenticated && auth.user && isValidChatId(chatId) ? {
      id: chatId,
      userId: String(auth.user._id),
      legacyUserId: auth.user.legacyId
    } : "skip"
  );

  const convexSpaces: Space[] = useQuery(
    "spaces:getSpacesByUser" as any,
    auth.user?._id && auth.isAuthenticated ? { userId: String(auth.user._id), legacyUserId: auth.user.legacyId } : "skip"
  );
  
  const convexMessages: ConvexMessage[] = useQuery(
    "messages:getMessagesByChatTest" as any,
    chatId && chatId !== 'new' && auth.isAuthenticated && auth.user && isValidChatId(chatId) ? {
      chatId,
      userId: String(auth.user._id),
      legacyUserId: auth.user.legacyId
    } : "skip"
  );


  const rawCurrentSpaceChats: MinimizedChat[] | undefined = useQuery(
    "chats:getChatsBySpaceId" as any,
    currentSpaceId && auth.user?._id && auth.isAuthenticated
      ? { spaceId: currentSpaceId, userId: String(auth.user._id), legacyUserId: auth.user.legacyId }
      : "skip"
  );

  const currentSpaceChats = useMemo(() => rawCurrentSpaceChats || [], [rawCurrentSpaceChats]);
  

  const [currentChat, setCurrentChat] = useState<ConvexChat | null>(null);
  const [currentChatSpace, setCurrentChatSpace] = useState<Space | null>(null);
  const [chatNotExist, setChatNotExist] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConvexMessage[]>([]);
  const [messagesError, setMessagesError] = useState<Error | null>(null);
  const [memoizedChatTitle, setMemoizedChatTitle] = useState('');
  const [quotedContent, setQuotedContent] = useState<string>('');
  const [tempChats, setTempChats] = useState<MinimizedChat[]>([]);
  
  // Space state
  const [currentSpace, setCurrentSpace] = useState<Space | null>(null);
  const [instructions, setInstructions] = useState('');
  const [title, setTitle] = useState<string>('');

  // UI state
  const [isSpaceChat, setIsSpaceChat] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [salesModalFeature, setSalesModalFeature] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [isCreatingSpace, setIsCreatingSpace] = useState(false);
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentThinkingTime, setCurrentThinkingTime] = useState(0);
  const [useReasoning, setUseReasoning] = useState(false);
  const [useWeb, setUseWeb] = useState(false);
  const [webCapable, setWebCapableState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('capability.webCapable');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const [resourceCapable, setResourceCapableState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('capability.resourceCapable');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const setWebCapable = useCallback((value: boolean) => {
    setWebCapableState(value);
    try { localStorage.setItem('capability.webCapable', String(value)); } catch {}
  }, []);
  const setResourceCapable = useCallback((value: boolean) => {
    setResourceCapableState(value);
    try { localStorage.setItem('capability.resourceCapable', String(value)); } catch {}
  }, []);
  
  // Incognito (private) chat state
  const [isIncognito, setIsIncognito] = useState(false);
  
  // ===== DERIVED STATE =====
  // Transform convex data for minimized chats
  const chats: MinimizedChat[] = useMemo(() => {
    const convexChatsList = convexChats && Array.isArray(convexChats) ? convexChats : [];
    
    // Create a map to prioritize temp chats over convex chats
    const finalChats = new Map<string, MinimizedChat>();
    
    // First add all convex chats (already minimized from backend)
    convexChatsList.forEach(chat => {
      finalChats.set(chat.id, chat);
    });
    
    // Then override with temp chats (for optimistic updates)
    tempChats.forEach(chat => {
      finalChats.set(chat.id, chat);
    });
    
    return Array.from(finalChats.values());
  }, [convexChats, tempChats]);
  
  // Clean up temp (optimistic) chats once Convex data has caught up.
  //
  // A temp chat is kept while either:
  //   - no Convex chat with that id exists yet (the create/mutation hasn't
  //     landed in the subscription update), OR
  //   - the Convex chat exists but the optimistically-mutated fields don't yet
  //     match what the optimistic update set (currently only `isPinned`).
  //
  // If `togglePinChat` were ever extended to mutate other fields, add them to
  // the comparison below so the temp chat is held until those also reconcile.
  useEffect(() => {
    if (!convexChats || !Array.isArray(convexChats) || tempChats.length === 0) return;

    setTempChats(prev => {
      const remaining = prev.filter(tempChat => {
        const convexChat = convexChats.find((c: MinimizedChat) => c.id === tempChat.id);
        if (!convexChat) return true; // backend hasn't seen this yet — keep
        // Compare every optimistically-mutable field. Add new fields here
        // when adding new optimistic mutations. Coerce via `!!` so an
        // unset `isPinned` on the Convex side matches an explicit `false`
        // on the optimistic temp chat (newly-created chats have no
        // `isPinned` field until the user pins them).
        const reconciled = !!convexChat.isPinned === !!tempChat.isPinned;
        return !reconciled;
      });
      return remaining.length === prev.length ? prev : remaining;
    });
  }, [convexChats, tempChats]);
  
  const spaces: Space[] = useMemo(() => {
    if (convexSpaces && Array.isArray(convexSpaces)) {
      return convexSpaces;
    }
    return [];
  }, [convexSpaces]);
  
  // Loading states
  const isChatsLoaded = Boolean(convexChats && Array.isArray(convexChats));
  const isChatsLoading = Boolean(
    convexChats === undefined && 
    auth.user?._id && 
    auth.isAuthenticated
  );
  const isCurrentChatLoading = Boolean(
    fullCurrentChat === undefined &&
    chatId &&
    chatId !== 'new' &&
    isValidChatId(chatId) &&
    auth.isAuthenticated &&
    auth.user
  );
  const isSpacesLoading = Boolean(
    convexSpaces === undefined && 
    auth.user?._id && 
    auth.isAuthenticated
  );
  const isSpaceChatsLoading = Boolean(
    rawCurrentSpaceChats === undefined &&
    currentSpaceId &&
    auth.user?._id &&
    auth.isAuthenticated
  );
  const isLoadingMessages = Boolean(
    convexMessages === undefined &&
    chatId &&
    chatId !== 'new' &&
    auth.isAuthenticated &&
    auth.user &&
    isValidChatId(chatId)
  );
  
  const filteredSpaces = useMemo(() => {
    return Array.isArray(spaces) ? spaces : [];
  }, [spaces]);
  
  // UI derived state
  const isSpaceView = useMemo(
    () =>
      location.pathname.startsWith('/spaces'),
    [location.pathname]
  );
  const isGeneratingResponse = useMemo(() => {
    return messages.some(message => message.isGenerating);
  }, [messages]);
  
  // Message branching (using existing hook)
  const { 
    globalBranchCount, 
    setGlobalBranchCount, 
    branchesMap, 
    filteredMessages, 
    maxBranchId, 
    activeBranches,
    setActiveBranchForPair
  } = useMessageGroups(messages, chatId || null);
  
  // ===== EFFECTS =====
  // Sync Convex messages directly when not submitting
  useEffect(() => {
    // Incognito mode keeps an in-memory transcript and never touches Convex,
    // so skip every code path here that would clobber the optimistic messages.
    if (isIncognito) {
      return;
    }
    try {
      if (chatId === 'new') {
        setMessages([]);
        setMessagesError(null);
      } else if (!chatId || !auth.isAuthenticated) {
        return;
      } else if (convexMessages instanceof Error) {
        setMessagesError(convexMessages);
      } else if (Array.isArray(convexMessages) && !isStreaming && !isSubmitting) {
        setMessages(convexMessages);
        setMessagesError(null);
      } else if ((convexMessages === null || (Array.isArray(convexMessages) && convexMessages.length === 0)) && !isStreaming && !isSubmitting) {
        setMessages([]);
        setMessagesError(null);
      }
    } catch (error) {
      setMessagesError(error as Error);
    }
    // Use `auth.user?._id` rather than `auth.user` so the effect doesn't
    // re-run (and potentially clobber in-flight messages) when AuthContext
    // rebuilds the user object reference without the id changing.
  }, [convexMessages, chatId, isStreaming, isSubmitting, auth.isAuthenticated, auth.user?._id, isIncognito]);
  
  // Sync current chat title
  useEffect(() => {
    if (chatId && chats.length > 0) {
      const chat = chats.find((chat: MinimizedChat) => chat.id === chatId);
      setMemoizedChatTitle(chat?.title || '');
    } else if (chatId === 'new') {
      setMemoizedChatTitle('');
    }
  }, [chatId, chats]);
  
  // Sync current space
  useEffect(() => {
    if (spaces) {
      const foundSpace = spaces.find((space: Space) => space.id === currentSpaceId) || null;
      setCurrentSpace(foundSpace);
    }
  }, [spaces, currentSpaceId]);
  
  // Update form state when space changes
  useEffect(() => {
    setInstructions(currentSpace?.instructionText || '');
    setTitle(currentSpace?.title || '');
  }, [currentSpace]);
  
  // Sync isSpaceChat state
  useEffect(() => {
    setIsSpaceChat(Boolean(currentChat?.spaceId));
  }, [currentChat]);
  
  // Handle chat changes
  useEffect(() => {
    if (!chatId) {
      if (!isIncognito) {
        setMessages([]);
      }
      setCurrentChat(null);
      setCurrentChatSpace(null);
    }
  }, [chatId, isIncognito]);

  // Reset *chat-scoped UI* state on chat change so an active edit cursor or
  // quoted selection doesn't leak from chat A into chat B. Deliberately does
  // NOT touch submission/streaming/thinking/tool state — the new-chat send
  // flow drives the chatId transition itself (navigate(`/${newId}`) fires
  // inside `handleSubmit` after `setIsSubmitting(true)`), and clearing those
  // flags here unblocks the messages-sync effect to wipe the optimistic
  // user/AI messages with the new chat's empty Convex result, producing a
  // visible flicker (message → empty → message).
  useEffect(() => {
    setEditingMessageId(null);
    setQuotedContent('');
  }, [chatId]);

  // Clear in-flight submission state when the user navigates between two
  // *different* chats. Without this, a submission left mid-flight on chat A
  // keeps `isSubmitting` true after the user clicks chat B in the sidebar;
  // the messages-sync effect refuses to swap to B's messages while
  // `isSubmitting` is set, so the UI looks frozen on A and the click appears
  // to do nothing.
  //
  // We must *not* run this on the undefined → new-chat-id transition —
  // that's the new-chat send flow's own navigation, and clearing the flag
  // there causes the message → empty → message flicker the previous attempt
  // introduced. Tracking the previous chatId in a ref lets us distinguish
  // the two cases cleanly.
  const prevChatIdRef = useRef<string | undefined>(chatId);
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    if (prev === chatId) return;            // initial mount, no real transition
    if (!prev && chatId) return;            // new-chat creation flow — keep submission state
    if (chatId) {
      // Chat→chat switch: drop the old transcript so the skeleton shows
      // instead of the previous chat while the new Convex query loads.
      setMessages([]);
      setMessagesError(null);
    }
    setIsSubmitting(false);
    setCurrentThinkingTime(0);
  }, [chatId]);

  // Streaming cleanup on chat transitions. Uses a ref so this effect only
  // re-runs when chatId changes (not on every isStreaming flip), and the
  // cleanup form means it never fires on initial mount — which matters
  // because the new-chat send flow flips chatId for the first time while a
  // stream is being set up; we don't want to cancel that.
  const isStreamingRef = useRef(isStreaming);
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);
  useEffect(() => {
    return () => {
      if (isStreamingRef.current) {
        setIsStreaming(false);
      }
    };
  }, [chatId]);

  // Auto-exit incognito as soon as the user navigates to a real chat or space.
  // The `justEnteredIncognito` ref prevents an immediate flip-back when the
  // user toggles on from a real chat URL (`navigate('/')` is async, so the
  // effect's first run may still see the old chatId).
  const justEnteredIncognitoRef = useRef(false);
  useEffect(() => {
    if (justEnteredIncognitoRef.current) {
      justEnteredIncognitoRef.current = false;
      return;
    }
    if (!isIncognito) return;
    const onRealChat = !!chatId && chatId !== 'new' && isValidChatId(chatId);
    const onSpace = location.pathname.startsWith('/spaces');
    if (onRealChat || onSpace) {
      setIsIncognito(false);
      setMessages([]);
    }
  }, [chatId, location.pathname, isIncognito]);

  // ===== INCOGNITO ACTIONS =====
  const enterIncognito = useCallback(() => {
    justEnteredIncognitoRef.current = true;
    setIsIncognito(true);
    setMessages([]);
    setMessagesError(null);
    setMemoizedChatTitle('');
    setQuotedContent('');
  }, []);

  const exitIncognito = useCallback(() => {
    setIsIncognito(false);
    setMessages([]);
    setMessagesError(null);
  }, []);
  
  // Sync currentChat directly with fullCurrentChat from Convex
  useEffect(() => {
    if (chatId && chatId !== 'new' && fullCurrentChat) {
      setCurrentChat(fullCurrentChat);
      setChatNotExist(false);
    } else if (chatId === 'new' || !chatId) {
      setCurrentChat(null);
      setChatNotExist(false);
    } else if (chatId && chatId !== 'new' && fullCurrentChat === null && !isCurrentChatLoading) {
      // Check if this chat exists in tempChats (recently created) before marking as not found
      const chatExistsInTemp = tempChats.some(chat => chat.id === chatId);
      if (!chatExistsInTemp) {
        // Chat ID exists but no chat found and not loading - chat doesn't exist
        setChatNotExist(true);
      } else {
        setChatNotExist(false);
      }
    }
  }, [chatId, fullCurrentChat, isCurrentChatLoading, tempChats]);
  
  // Sync currentChatSpace with spaces data
  useEffect(() => {
    if (currentChat?.spaceId) {
      const foundSpace = spaces.find((space: Space) => space.id === currentChat.spaceId);
      setCurrentChatSpace(foundSpace || null);
    } else {
      setCurrentChatSpace(null);
    }
  }, [currentChat, spaces]);
  
  
  // Migration effect: Migrate localStorage pinned chats to backend.
  // Track migration per-user so a sign-out / sign-in as a different user in
  // the same tab still triggers migration for the new user. We depend on
  // `convexChats` rather than the derived `chats` so this doesn't re-fire on
  // every pin toggle (which mutates `tempChats` → recomputes `chats`).
  const migratedUserIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const userId = auth.user?._id;
    if (!auth.isAuthenticated || !userId) return;
    if (!convexChats || !Array.isArray(convexChats) || convexChats.length === 0) return;
    if (migratedUserIdsRef.current.has(String(userId))) return;

    const migratePinnedChats = async () => {
      try {
        const localPinnedChats = localStorage.getItem('pinnedChats');
        if (!localPinnedChats) {
          migratedUserIdsRef.current.add(String(userId));
          return;
        }

        const pinnedChatIds = JSON.parse(localPinnedChats);
        if (!Array.isArray(pinnedChatIds) || pinnedChatIds.length === 0) {
          migratedUserIdsRef.current.add(String(userId));
          return;
        }

        const migrationPromises = pinnedChatIds.map(async (chatId: string) => {
          const chat = convexChats.find((c: MinimizedChat) => c.id === chatId);
          if (chat && !chat.isPinned) {
            try {
              await pinChatMutation({ id: chatId, userId });
            } catch (error) {
              console.error(`Failed to pin chat ${chatId}:`, error);
            }
          }
        });

        await Promise.all(migrationPromises);
        localStorage.removeItem('pinnedChats');
        migratedUserIdsRef.current.add(String(userId));
      } catch (error) {
        console.error('Error during pinned chats migration:', error);
      }
    };

    migratePinnedChats();
  }, [convexChats, auth.isAuthenticated, auth.user?._id, pinChatMutation]);
  
  
  // Space attachments processing
  const getSpaceResources = useCallback((): Resource[] => {
    const resourcesText = currentSpace?.artifactsText || '';
    if (!resourcesText) return [];

    const resourceMatches = resourcesText.match(/<(?:artifacts|resource)>([\s\S]*?)<\/(?:artifacts|resource)>/g) || [];

    // Ids derive from the block's position so they stay stable across renders
    // (they are used as React keys).
    return resourceMatches.flatMap((match: string, index: number) => {
      const chatIdMatch = match.match(/<chatid>(.*?)<\/chatid>/);
      const chatId = chatIdMatch ? chatIdMatch[1] : null;
      
      const resources: Resource[] = [];
      
      // Process essays
      const essayMatch = match.match(/<essay>([\s\S]*?)<\/essay>/);
      if (essayMatch) {
        const essayContent = essayMatch[1];
        const titleMatch = essayContent.match(/<title>(.*?)<\/title>/);
        const contentMatch = essayContent.match(/<content>([\s\S]*?)<\/content>/);
        
        if (titleMatch && contentMatch) {
          resources.push({
            title: titleMatch[1],
            type: 'essay',
            content: contentMatch[1],
            timestamp: new Date().toISOString(),
            id: `essay_${index}`,
            chatId: chatId || '',
          });
        }
      }
      
      // Process code resources
      const codeRegex = /<code(?:\s+[^>]*?(?:language="([^"]*)")?[^>]*?(?:title="([^"]*)")?[^>]*?|[^>]*)>([\s\S]*?)<\/code>/;
      const codeMatch = match.match(codeRegex);
      if (codeMatch) {
        const language = codeMatch[1] || 'text';
        const title = codeMatch[2] || 'Code Snippet';
        const codeContent = codeMatch[3] || '';
        
        resources.push({
          title: title,
          type: 'code',
          id: `code_${index}`,
          chatId: chatId || '',
          content: codeContent,
          language: language,
          timestamp: new Date().toISOString(),
        });
      }
      
      // Process visualization resources
      const vizRegex = /<visualization(?:\s+[^>]*?(?:title="([^"]*)")?[^>]*?|[^>]*)>([\s\S]*?)<\/visualization>/;
      const vizMatch = match.match(vizRegex);
      if (vizMatch) {
        const title = vizMatch[1] || 'Visualization';
        const vizContent = vizMatch[2] || '';
        
        resources.push({
          title: title,
          type: 'visualization',
          id: `viz_${index}`,
          chatId: chatId || '',
          content: vizContent,
          timestamp: new Date().toISOString(),
        });
      }
      
      return resources;
    });
  }, [currentSpace?.artifactsText]);

  const spaceAttachments = useMemo<Resource[]>(() => {
    const textAttachments: Resource[] = (currentSpace?.textAttachments || []).map((doc) => ({
      id: doc.url || doc.title,
      title: doc.title,
      type: doc.type,
      content: doc.content || '',
      url: doc.url,
      timestamp: new Date().toISOString(),
      chatId: currentSpaceId || '',
      isTextAttachment: true,
    }));
    return [...textAttachments, ...getSpaceResources()];
  }, [currentSpace?.textAttachments, getSpaceResources, currentSpaceId]);

  // Helper function for consistent error handling
  const handleError = useCallback((error: any, message: string) => {
    console.error(`${message}:`, error);
    toast(`${message}. Please try again.`, 'error');
  }, [toast]);

  // Chat actions
  const addNewChat = useCallback(async (spaceId?: string): Promise<{ id: string }> => {
    const timestamp = new Date().toISOString();
    
    if (auth.isAuthenticated && auth.user) {
      let id: string;
      try {
        id = await createChatMutation({
          title: 'New Chat',
          createdAt: timestamp,
          lastModified: timestamp,
          userId: auth.user._id,
          spaceId: spaceId || null,
          deletedAt: null,
        });
      } catch (error) {
        console.error('Failed to create chat:', error);
        toast('Failed to create chat. Please try again.', 'error');
        throw error;
      }

      // Optimistic temp chat: show the new chat in the sidebar immediately
      // and protect the `chatNotExist` guard at the chat route from a brief
      // window where the Convex subscription hasn't yet propagated the
      // newly-created chat. The temp-chat cleanup effect removes this entry
      // as soon as the same id appears in `convexChats` with matching
      // optimistically-mutable fields.
      const optimisticChat: MinimizedChat = {
        id,
        title: 'New Chat',
        lastModified: timestamp,
        spaceId: spaceId || undefined,
        isPinned: false,
      };
      setTempChats(prev =>
        prev.some(c => c.id === id) ? prev : [optimisticChat, ...prev]
      );

      return { id };
    }
    throw new Error('User not authenticated');
  }, [createChatMutation, auth.user, auth.isAuthenticated, toast]);
  
  // Deletes a chat on the backend. Note: this does NOT redirect the user if
  // they are currently viewing the deleted chat — callers are responsible for
  // navigation (the sidebar's `handleChatRemove` picks the next chat in the
  // list). If you call this from a context where the user can be on the
  // deleted chat's URL, navigate yourself or the user will land on a dead
  // route until `chatNotExist` triggers an upstream redirect.
  const removeChat = useCallback(async (id: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await deleteChatMutation({ id });
      trackChatDeleted({
        chatId: id,
        messageCount: id === chatId ? messages.length : undefined
      });
    } catch (error) {
      handleError(error, 'Failed to delete chat');
    }
  }, [deleteChatMutation, auth.isAuthenticated, auth.user?._id, handleError, chatId, messages, trackChatDeleted]);

  const renameChat = useCallback(async (id: string, newTitle: string) => {
    if (!id) return;
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await updateChatMutation({
        id,
        updates: {
          title: newTitle,
          lastModified: new Date().toISOString()
        }
      });
      trackChatRenamed({ chatId: id });
    } catch (error) {
      handleError(error, 'Failed to rename chat');
    }
  }, [updateChatMutation, auth.isAuthenticated, auth.user?._id, handleError, trackChatRenamed]);

  const addChatToSpace = useCallback(async (id: string, spaceId: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await updateChatMutation({
        id,
        updates: {
          spaceId,
          lastModified: new Date().toISOString()
        }
      });
    } catch (error) {
      handleError(error, 'Failed to add chat to space');
    }
  }, [updateChatMutation, auth.isAuthenticated, auth.user?._id, handleError]);
  
  const removeChatFromSpace = useCallback(async (id: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await updateChatMutation({
        id,
        updates: {
          spaceId: null,
          lastModified: new Date().toISOString()
        }
      });
    } catch (error) {
      handleError(error, 'Failed to remove chat from space');
    }
  }, [updateChatMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  // Space actions
  const addNewSpace = useCallback(async (title?: string, instructions?: string): Promise<Space> => {
    const userId = auth.user?._id ?? '';
    
    if (auth.isAuthenticated && userId) {
      try {
        const newSpace = await createSpaceMutation({
          title: title || null,
          userId,
          instructionText: instructions || '',
        }) as Space;
        
        // Track space creation
        trackSpaceCreated({
          spaceId: newSpace.id,
          hasAttachments: false
        });
        return newSpace;
      } catch (error) {
        console.error('Failed to create space:', error);
        toast('Failed to create space. Please try again.', 'error');
        throw error;
      }
    }
    throw new Error('User not authenticated');
  }, [createSpaceMutation, auth.user?._id, auth.isAuthenticated, toast, trackSpaceCreated]);
  
  const updateSpaceTitle = useCallback(async (spaceId: string, title: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await updateSpaceMutation({
        spaceId,
        updates: { title }
      });
    } catch (error) {
      handleError(error, 'Failed to update space title');
    }
  }, [updateSpaceMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  const updateSpaceInstructions = useCallback(async (spaceId: string, instructions: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await updateSpaceMutation({
        spaceId,
        updates: { instructionText: instructions }
      });
    } catch (error) {
      handleError(error, 'Failed to update space instructions');
    }
  }, [updateSpaceMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  // Materials and saved resources are merged on the server in one mutation,
  // so concurrent uploads (other tabs, devices) can't overwrite each other and
  // the capacity limit is enforced there.
  const addResourceToSpace = useCallback(async (spaceId: string, content: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await appendSpaceResourceMutation({ spaceId, content });
    } catch (error) {
      handleError(error, 'Failed to add resource to space');
    }
  }, [appendSpaceResourceMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  const addTextAttachmentsToSpace = useCallback(async (spaceId: string, docs: MessageDocument[]) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    if (docs.length === 0) return;
    try {
      await addSpaceAttachmentsMutation({
        spaceId,
        docs: docs.map(({ title, url, type, content, tokens }) => ({
          title,
          url,
          type,
          ...(content ? { content } : {}),
          ...(typeof tokens === 'number' ? { tokens } : {}),
        })),
      });
    } catch (error) {
      handleError(error, 'Failed to add attachments to space');
      throw error;
    }
  }, [addSpaceAttachmentsMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  const removeTextAttachmentFromSpace = useCallback(async (spaceId: string, url: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      await removeSpaceAttachmentMutation({ spaceId, url });
    } catch (error) {
      handleError(error, 'Failed to remove attachment');
      throw error;
    }
  }, [removeSpaceAttachmentMutation, auth.isAuthenticated, auth.user?._id, handleError]);

  // The server computes a space's knowledge count when materials are added or
  // removed (add/removeSpaceAttachments) and no longer accepts it from clients,
  // so this only keeps the signature for existing callers.
  const updateSpaceKnowledgeCount = useCallback(async (_spaceId: string, _knowledgeCount: number) => {}, []);
  
  const deleteSpace = useCallback(async (spaceId: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    try {
      // The chat count is only known for the space being viewed.
      const chatCount = spaceId === currentSpaceId ? currentSpaceChats.length : undefined;
      const isViewingSpace = spaceId === currentSpaceId || currentChat?.spaceId === spaceId;

      // Also deletes the space's chats on the server.
      await deleteSpaceMutation({ spaceId });

      trackSpaceDeleted({ spaceId, chatCount });
      if (isViewingSpace) navigate('/spaces');

      toast('Space deleted successfully', 'success');
    } catch (error) {
      handleError(error, 'Failed to delete space');
    }
  }, [deleteSpaceMutation, auth.isAuthenticated, auth.user?._id, toast, handleError, currentSpaceId, currentSpaceChats, currentChat?.spaceId, navigate, trackSpaceDeleted]);
  

  const startStreaming = useCallback(() => {
    setIsStreaming(true);
  }, []);

  const rateMessage = useCallback(async (messageId: string, status: 'like' | 'dislike') => {
    const message = messages.find(msg => msg._id === messageId);
    const previousMessages = messages;

    // Optimistic UI update — applied for all users; unauthenticated raters
    // keep the in-memory rating for the session even though it isn't persisted.
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg._id === messageId
          ? { ...msg, isLiked: status === 'like', isDisliked: status === 'dislike' }
          : msg
      )
    );

    // Always report disliked content regardless of auth — flagging bad output
    // is useful even from anonymous sessions.
    const reportPromise = status === 'dislike'
      ? (async () => {
          try {
            await reportMessageMutation({
              content: message?.content || '',
              mode: message?.mode ? JSON.stringify(message.mode) : null,
              model: message?.model ?? null,
              searchItems: message?.searchItems ? JSON.stringify(message.searchItems) : null,
            });
          } catch (error) {
            console.error('Failed to report disliked message:', error);
          }
        })()
      : Promise.resolve();

    if (auth.isAuthenticated && auth.user) {
      try {
        const ratePromise = isTempMessageId(messageId)
          ? Promise.resolve()
          : rateMessageMutation({ id: messageId, status });
        await Promise.all([ratePromise, reportPromise]);
      } catch (error) {
        console.error('Error rating message:', error);
        toast('Failed to rate message. Please try again.', 'error');
        setMessages(previousMessages);
      }
    } else {
      await reportPromise;
    }
  }, [rateMessageMutation, auth.isAuthenticated, auth.user, toast, messages, setMessages, reportMessageMutation]);
  
  
  const retryMessages = useCallback(() => {
    setMessagesError(null);
  }, []);
  
  // UI actions
  const closeSalesModal = useCallback(() => {
    setShowSalesModal(false);
    setSalesModalFeature('');
  }, []);
  
  const openUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);
  
  const closeUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);
  
  const toggleReasoning = useCallback(() => {
    setUseReasoning(prev => !prev);
  }, []);
  
  const toggleWebSearch = useCallback(() => {
    setUseWeb(prev => !prev);
  }, []);
  
  const syncThinkingTime = useCallback((time: number) => {
    setCurrentThinkingTime(time);
  }, []);
  
  const togglePinChat = useCallback(async (id: string) => {
    if (!auth.isAuthenticated || !auth.user?._id) return;
    
    const chat = chats.find(c => c.id === id);
    if (!chat) return;
    
    const originalPinState = chat.isPinned;
    const newPinState = !originalPinState;
    
    // Optimistic update: immediately update the chat in tempChats
    setTempChats(prev => {
      const existingTempChatIndex = prev.findIndex(c => c.id === id);
      if (existingTempChatIndex >= 0) {
        // Update existing temp chat
        const updatedTempChats = [...prev];
        updatedTempChats[existingTempChatIndex] = {
          ...updatedTempChats[existingTempChatIndex],
          isPinned: newPinState
        };
        return updatedTempChats;
      } else {
        // Add optimistic temp chat
        const optimisticChat = {
          ...chat,
          isPinned: newPinState
        };
        return [optimisticChat, ...prev];
      }
    });
    
    try {
      if (originalPinState) {
        await unpinChatMutation({ id, userId: auth.user._id });
      } else {
        await pinChatMutation({ id, userId: auth.user._id });
      }
      // Temp chat cleanup is handled reactively by the effect below
    } catch (error) {
      console.error('Error toggling pin status:', error);
      
      // Rollback optimistic update on error
      setTempChats(prev => {
        const existingTempChatIndex = prev.findIndex(c => c.id === id);
        if (existingTempChatIndex >= 0) {
          const updatedTempChats = [...prev];
          updatedTempChats[existingTempChatIndex] = {
            ...updatedTempChats[existingTempChatIndex],
            isPinned: originalPinState
          };
          return updatedTempChats;
        }
        return prev;
      });
    }
  }, [auth.isAuthenticated, auth.user?._id, chats, pinChatMutation, unpinChatMutation]);
  
  // Space-specific handlers
  const handleUpdateSpaceInstructions = useCallback(async () => {
    if (!currentSpace?.id || instructions === currentSpace.instructionText) {
      return;
    }
    
    await updateSpaceInstructions(currentSpace.id, instructions);
  }, [currentSpace, instructions, updateSpaceInstructions]);
  
  const handleUpdateSpaceTitle = useCallback(async () => {
    if (!currentSpace?.id || !title.trim() || title === currentSpace.title) {
      setIsEditingTitle(false);
      return;
    }
    
    await updateSpaceTitle(currentSpace.id, title);
    setIsEditingTitle(false);
  }, [currentSpace, title, updateSpaceTitle]);
  
  const handleCreateSpaceChat = useCallback(async (isInChat?: boolean) => {
    if (isInChat && messages.length === 0) {
      return;
    }
    
    // Space chats live at `/<chatId>`, where the URL carries no space id, so
    // fall back to the open chat's space.
    const spaceId = currentSpaceId || currentChat?.spaceId || '';
    if (!spaceId) return;

    const newChat = await addNewChat(spaceId);
    
    // Track space chat creation
    trackSpaceChatCreated({
      spaceId,
      chatId: newChat.id
    });
    
    navigate(`/${newChat.id}`);
  }, [messages.length, currentSpaceId, currentChat?.spaceId, addNewChat, navigate, trackSpaceChatCreated]);
  
  const handleUpdateSpaceKnowledgeCount = useCallback(async (knowledgeCount: number) => {
    if (!currentSpace?.id) return;

    const previousSpace = currentSpace;
    setCurrentSpace({ ...currentSpace, knowledgeCount });
    try {
      await updateSpaceKnowledgeCount(currentSpace.id, knowledgeCount);
    } catch (error) {
      // Roll back the optimistic update so the UI doesn't keep showing the
      // bumped count after a backend failure. `updateSpaceKnowledgeCount`
      // already toasts the error; we just restore state here.
      setCurrentSpace(previousSpace);
      throw error;
    }
  }, [currentSpace, updateSpaceKnowledgeCount]);

  // ===== CONTEXT VALUE =====
  const contextValue = useMemo<AppDataContextType>(() => ({
    // Data
    chats,
    isChatsLoaded,
    isChatsLoading,
    currentChat,
    isCurrentChatLoading,
    chatNotExist,
    messages,
    isLoadingMessages,
    messagesError,
    currentChatId: chatId || null,
    spaces,
    isSpacesLoading,
    isSpaceChatsLoading,
    filteredSpaces,
    currentSpace,
    currentSpaceId,
    currentChatSpace,
    currentSpaceChats,
    spaceAttachments,

    // UI State
    isSpaceChat,
    isSpaceView,
    memoizedChatTitle,
    quotedContent,
    isSearchModalOpen,
    showSalesModal,
    salesModalFeature,
    isUploadModalOpen,
    isEditingTitle,
    editingMessageId,
    title,
    instructions,
    isCreatingSpace,
    activeBranches,
    globalBranchCount,
    branchesMap,
    filteredMessages,
    maxBranchId,
    isStreaming,
    isSubmitting,
    isGeneratingResponse,
    currentThinkingTime,
    useReasoning,
    useWeb,
    webCapable,
    resourceCapable,
    isIncognito,

    addNewChat,
    removeChat,
    renameChat,
    addChatToSpace,
    removeChatFromSpace,
    addNewSpace,
    updateSpaceTitle,
    updateSpaceInstructions,
    addResourceToSpace,
    updateSpaceKnowledgeCount,
    deleteSpace,
    addTextAttachmentsToSpace,
    removeTextAttachmentFromSpace,
    setMessages,
    retryMessages,
    startStreaming,
    rateMessage,
    setActiveBranchForPair,
    setIsSpaceChat,
    setMemoizedChatTitle,
    setQuotedContent,
    setIsSearchModalOpen,
    setSalesModalFeature,
    setShowSalesModal,
    closeSalesModal,
    openUploadModal,
    closeUploadModal,
    setIsEditingTitle,
    setEditingMessageId,
    setTitle,
    setInstructions,
    setIsCreatingSpace,
    setGlobalBranchCount,
    setIsSubmitting,
    setCurrentThinkingTime,
    syncThinkingTime,
    toggleReasoning,
    toggleWebSearch,
    setUseWeb,
    setWebCapable,
    setResourceCapable,
    setIsStreaming,
    togglePinChat,
    setCurrentChat,
    setCurrentChatSpace,
    setCurrentSpace,
    handleUpdateSpaceInstructions,
    handleUpdateSpaceTitle,
    handleCreateSpaceChat,
    handleUpdateSpaceKnowledgeCount,
    enterIncognito,
    exitIncognito,
  }), [
    chats, isChatsLoaded, isChatsLoading, currentChat, isCurrentChatLoading, chatNotExist, messages, isLoadingMessages, messagesError, chatId,
    spaces, isSpacesLoading, isSpaceChatsLoading, filteredSpaces, currentSpace, currentSpaceId, currentChatSpace, currentSpaceChats, spaceAttachments,
    isSpaceChat, isSpaceView, memoizedChatTitle, quotedContent, isSearchModalOpen,
    showSalesModal, salesModalFeature, isUploadModalOpen, isEditingTitle, editingMessageId, title, instructions, isCreatingSpace,
    activeBranches, globalBranchCount, branchesMap, filteredMessages, maxBranchId,
    isStreaming, isSubmitting, isGeneratingResponse, currentThinkingTime, useReasoning, useWeb, webCapable, resourceCapable,
    isIncognito, enterIncognito, exitIncognito,
    addNewChat, removeChat, renameChat, addChatToSpace, removeChatFromSpace, addNewSpace, updateSpaceTitle, updateSpaceInstructions,
    addResourceToSpace, updateSpaceKnowledgeCount, deleteSpace, addTextAttachmentsToSpace, removeTextAttachmentFromSpace,
    startStreaming, rateMessage, setActiveBranchForPair,
    closeSalesModal, openUploadModal, closeUploadModal,
    syncThinkingTime, toggleReasoning, toggleWebSearch, setWebCapable, setResourceCapable, togglePinChat, setIsStreaming,
    handleUpdateSpaceInstructions, handleUpdateSpaceTitle, handleCreateSpaceChat,
    handleUpdateSpaceKnowledgeCount, retryMessages, setGlobalBranchCount,
  ]);
  return (
    <AppDataContext.Provider value={contextValue}>
      {children}
    </AppDataContext.Provider>
  );
};

// Custom hooks for accessing specific parts of the context
export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }
  return context;
};

// Backward compatibility hooks. Each return value is memoised so consumers
// don't re-render on every parent render — only when one of their selected
// fields actually changes. The whole context object still updates on any
// state change, but useMemo's dependency list narrows that to the subset each
// hook exposes.
export const useUnifiedData = () => {
  const context = useAppData();
  return useMemo(() => ({
    chats: context.chats,
    isChatsLoaded: context.isChatsLoaded,
    isChatsLoading: context.isChatsLoading,
    spacesData: context.spaces,
    isSpacesLoading: context.isSpacesLoading,
    filteredSpaces: context.filteredSpaces,
    addNewChat: context.addNewChat,
    addNewSpace: context.addNewSpace,
    removeChat: context.removeChat,
    renameChat: context.renameChat,
    addChatToSpace: context.addChatToSpace,
    removeChatFromSpace: context.removeChatFromSpace,
    updateSpaceInstructions: context.updateSpaceInstructions,
    addResourceToSpace: context.addResourceToSpace,
    updateSpaceTitle: context.updateSpaceTitle,
    updateSpaceKnowledgeCount: context.updateSpaceKnowledgeCount,
    deleteSpace: context.deleteSpace,
    addTextAttachmentsToSpace: context.addTextAttachmentsToSpace,
    removeTextAttachmentFromSpace: context.removeTextAttachmentFromSpace,
    togglePinChat: context.togglePinChat,
  }), [
    context.chats, context.isChatsLoaded, context.isChatsLoading, context.spaces,
    context.isSpacesLoading, context.filteredSpaces,
    context.addNewChat, context.addNewSpace, context.removeChat, context.renameChat,
    context.addChatToSpace, context.removeChatFromSpace, context.updateSpaceInstructions, context.addResourceToSpace,
    context.updateSpaceTitle, context.updateSpaceKnowledgeCount, context.deleteSpace,
    context.addTextAttachmentsToSpace, context.removeTextAttachmentFromSpace,
    context.togglePinChat,
  ]);
};

export const useChatContext = () => {
  const context = useAppData();
  return useMemo(() => ({
    messages: context.messages,
    setMessages: context.setMessages,
    messagesError: context.messagesError,
    isLoadingMessages: context.isLoadingMessages,
    retryMessages: context.retryMessages,
    activeBranches: context.activeBranches,
    setActiveBranchForPair: context.setActiveBranchForPair,
    isSpaceChat: context.isSpaceChat,
    setIsSpaceChat: context.setIsSpaceChat,
    salesModalFeature: context.salesModalFeature,
    setSalesModalFeature: context.setSalesModalFeature,
    showSalesModal: context.showSalesModal,
    setShowSalesModal: context.setShowSalesModal,
    memoizedChatTitle: context.memoizedChatTitle,
    setMemoizedChatTitle: context.setMemoizedChatTitle,
    isSearchModalOpen: context.isSearchModalOpen,
    setIsSearchModalOpen: context.setIsSearchModalOpen,
    globalBranchCount: context.globalBranchCount,
    setGlobalBranchCount: context.setGlobalBranchCount,
    branchesMap: context.branchesMap,
    filteredMessages: context.filteredMessages,
    maxBranchId: context.maxBranchId,
    currentThinkingTime: context.currentThinkingTime,
    setCurrentThinkingTime: context.setCurrentThinkingTime,
    syncThinkingTime: context.syncThinkingTime,
    currentChatId: context.currentChatId,
    closeSalesModal: context.closeSalesModal,
    useReasoning: context.useReasoning,
    toggleReasoning: context.toggleReasoning,
    useWeb: context.useWeb,
    toggleWebSearch: context.toggleWebSearch,
    setUseWeb: context.setUseWeb,
    webCapable: context.webCapable,
    resourceCapable: context.resourceCapable,
    setWebCapable: context.setWebCapable,
    setResourceCapable: context.setResourceCapable,
    isStreaming: context.isStreaming,
    setIsStreaming: context.setIsStreaming,
    startStreaming: context.startStreaming,
    isSubmitting: context.isSubmitting,
    setIsSubmitting: context.setIsSubmitting,
    isGeneratingResponse: context.isGeneratingResponse,
    rateMessage: context.rateMessage,
    isSpaceView: context.isSpaceView,
    currentChat: context.currentChat,
    setCurrentChat: context.setCurrentChat,
    currentChatSpace: context.currentChatSpace,
    setCurrentChatSpace: context.setCurrentChatSpace,
    quotedContent: context.quotedContent,
    setQuotedContent: context.setQuotedContent,
    editingMessageId: context.editingMessageId,
    setEditingMessageId: context.setEditingMessageId,
    isIncognito: context.isIncognito,
    enterIncognito: context.enterIncognito,
    exitIncognito: context.exitIncognito,
  }), [
    context.messages, context.setMessages, context.messagesError, context.isLoadingMessages,
    context.retryMessages, context.activeBranches, context.setActiveBranchForPair,
    context.isSpaceChat, context.setIsSpaceChat, context.salesModalFeature,
    context.setSalesModalFeature, context.showSalesModal, context.setShowSalesModal,
    context.memoizedChatTitle, context.setMemoizedChatTitle,
    context.isSearchModalOpen, context.setIsSearchModalOpen, context.globalBranchCount,
    context.setGlobalBranchCount, context.branchesMap, context.filteredMessages,
    context.maxBranchId, context.currentThinkingTime, context.setCurrentThinkingTime,
    context.syncThinkingTime,
    context.currentChatId, context.closeSalesModal, context.useReasoning,
    context.toggleReasoning, context.useWeb, context.toggleWebSearch, context.setUseWeb,
    context.webCapable, context.resourceCapable, context.setWebCapable,
    context.setResourceCapable,
    context.isStreaming, context.setIsStreaming, context.startStreaming, context.isSubmitting,
    context.setIsSubmitting, context.isGeneratingResponse, context.rateMessage,
    context.isSpaceView, context.currentChat, context.setCurrentChat,
    context.currentChatSpace, context.setCurrentChatSpace, context.quotedContent,
    context.setQuotedContent, context.editingMessageId, context.setEditingMessageId,
    context.isIncognito, context.enterIncognito, context.exitIncognito,
  ]);
};

export const useSpaceContext = () => {
  const context = useAppData();
  return useMemo(() => ({
    currentSpace: context.currentSpace,
    setCurrentSpace: context.setCurrentSpace,
    handleUpdateSpaceInstructions: context.handleUpdateSpaceInstructions,
    handleUpdateSpaceTitle: context.handleUpdateSpaceTitle,
    isEditingTitle: context.isEditingTitle,
    setIsEditingTitle: context.setIsEditingTitle,
    currentSpaceId: context.currentSpaceId,
    title: context.title,
    setTitle: context.setTitle,
    instructions: context.instructions,
    setInstructions: context.setInstructions,
    currentSpaceChats: context.currentSpaceChats,
    isSpaceChatsLoading: context.isSpaceChatsLoading,
    isCreatingSpace: context.isCreatingSpace,
    setIsCreatingSpace: context.setIsCreatingSpace,
    isUploadModalOpen: context.isUploadModalOpen,
    openUploadModal: context.openUploadModal,
    closeUploadModal: context.closeUploadModal,
    spaceAttachments: context.spaceAttachments,
    removeTextAttachmentFromSpace: context.removeTextAttachmentFromSpace,
    addTextAttachmentsToSpace: context.addTextAttachmentsToSpace,
    handleCreateSpaceChat: context.handleCreateSpaceChat,
    handleUpdateSpaceKnowledgeCount: context.handleUpdateSpaceKnowledgeCount,
  }), [
    context.currentSpace, context.setCurrentSpace, context.handleUpdateSpaceInstructions,
    context.handleUpdateSpaceTitle, context.isEditingTitle, context.setIsEditingTitle,
    context.currentSpaceId, context.title, context.setTitle, context.instructions,
    context.setInstructions, context.currentSpaceChats, context.isSpaceChatsLoading,
    context.isCreatingSpace, context.setIsCreatingSpace, context.isUploadModalOpen,
    context.openUploadModal, context.closeUploadModal, context.spaceAttachments,
    context.removeTextAttachmentFromSpace, context.addTextAttachmentsToSpace,
    context.handleCreateSpaceChat, context.handleUpdateSpaceKnowledgeCount,
  ]);
};
